import { Image, useComputedColorScheme } from '@mantine/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes } from 'react-router-dom';
import { isOfficialLoginMethod, LOGIN_METHOD, type LoginRequest, type Session } from '@/api';
import { Login2FAPage } from '@/pages/Login2FAPage';
import { LoginCallbackPage } from '@/pages/LoginCallbackPage';
import { LoginPage } from '@/pages/LoginPage';
import { MagicLinkLoginPage } from '@/pages/MagicLinkLoginPage';
import { NewsPage } from '@/pages/NewsPage';
import { PrivacyPage } from '@/pages/PrivacyPage';
import { SignupPage } from '@/pages/SignupPage';
import { SupportPage } from '@/pages/SupportPage';
import { TermsPage } from '@/pages/TermsPage';
import { API_BASE_URL } from '../../constants';
import { AccountApiKeysPage } from '../../pages/Account/ApiKeys.page';
import { AccountBillingSubscriptionsPage } from '../../pages/Account/Billing/Subscriptions.page';
import { AccountBillingUsagePage } from '../../pages/Account/Billing/Usage.page';
import { AccountConnectedAccountsLandingPage } from '../../pages/Account/ConnectedAccountsLanding.page';
import { DataPrivacyPage } from '../../pages/Account/DataPrivacy.page';
import { AccountDocsPage } from '../../pages/Account/Docs.page';
import { AccountFormCreatePage } from '../../pages/Account/Forms/Create.page';
import { AccountFormFieldsPage } from '../../pages/Account/Forms/Fields.page';
import { AccountFormsStartPage } from '../../pages/Account/Forms/Start.page';
import { AccountFormViewPage } from '../../pages/Account/Forms/View.page';
import { AccountHomePage } from '../../pages/Account/Home.page';
import { AccountIntegrationCreatePage } from '../../pages/Account/Integrations/Create.page';
import { AccountIntegrationDeliveriesPage } from '../../pages/Account/Integrations/Deliveries.page';
import { AccountIntegrationsStartPage } from '../../pages/Account/Integrations/Start.page';
import { AccountIntegrationViewPage } from '../../pages/Account/Integrations/View.page';
import { AccountIssuesStartPage } from '../../pages/Account/Issues/Start.page';
import { AccountMcpPage } from '../../pages/Account/Mcp.page';
import { AccountMessageDetailPage } from '../../pages/Account/Messags/Detail.page';
import { AccountMessagesStartPage } from '../../pages/Account/Messags/Start.page';
import { AccountProfilePage } from '../../pages/Account/Profile.page';
import { AccountReferralsPage } from '../../pages/Account/Referrals.page';
import { AccountSecurityLogPage } from '../../pages/Account/SecurityLog.page';
import { DocsPage } from '../../pages/Docs.page';
import { HomePage } from '../../pages/Home.page';
import { IntegrationDetailPage } from '../../pages/IntegrationDetail.page';
import { IntegrationsPage } from '../../pages/Integrations.page';
import { PricingPage } from '../../pages/Pricing.page';
import { useRustyState } from '../../state';
import classes from '../Common/Title.module.css';
import { Footer } from './Footer';

interface LanguageRoutesProps {
  languagePrefix?: string;
}

export function LanguageRoutes({ languagePrefix }: LanguageRoutesProps) {
  const { t, i18n } = useTranslation();
  const computedColorScheme = useComputedColorScheme('light');
  const isDark = computedColorScheme === 'dark';

  const brandLogo = <Image src="/logo.svg" alt={t('brand.logoAlt')} w={48} h={48} fit="contain" />;
  const brandName = (
    <>
      {t('brand.name')}
      {t('brand.nameSecond')}
    </>
  );

  React.useEffect(() => {
    if (languagePrefix && languagePrefix !== i18n.language) {
      i18n.changeLanguage(languagePrefix);
    } else if (!languagePrefix && i18n.language !== 'en') {
      i18n.changeLanguage('en');
    }
  }, [languagePrefix, i18n]);

  // Refresh identity claims from /v1/a/whoami on boot and on
  // tab-focus / visibility transitions so the account widget shows the
  // freshest email / displayName without periodic polling. Debounced to a
  // 5-second minimum interval because focus events fire aggressively.
  React.useEffect(() => {
    const auth = useRustyState.getState().api?.auth;
    if (!auth) return;

    let lastCallAt = 0;
    const minIntervalMs = 5_000;

    const callWhoami = () => {
      const now = Date.now();
      if (now - lastCallAt < minIntervalMs) return;
      if (!auth.hasValidAccessToken()) return;
      lastCallAt = now;
      auth.whoami().catch((err: unknown) => {
        console.warn('whoami refresh failed; non-fatal', err);
      });
    };

    // Boot: kick off once we know we have a valid access token. Don't block
    // initial render — the call is fire-and-forget.
    callWhoami();

    const onFocus = () => callWhoami();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') callWhoami();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const setSession = (session: Session) => {
    useRustyState.getState().api.auth?.setSession(session);
  };

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route
        path="/news"
        element={<NewsPage blogBaseUrl="https://blog.formshive.com" footer={<Footer />} />}
      />
      <Route path="/integrations" element={<IntegrationsPage />} />
      <Route path="/integrations/:slug" element={<IntegrationDetailPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/docs" element={<DocsPage />} />
      <Route
        path="/login"
        element={
          <LoginPage
            login={async (loginRequest: LoginRequest) => {
              if (isOfficialLoginMethod(loginRequest.type)) {
                return await useRustyState.getState().api.auth!.login(loginRequest);
              }
              throw new Error('Unsupported login method');
            }}
            loginChallenge={useRustyState.getState().loginChallenge}
            verify2FA={useRustyState.getState().verify2FA}
            logo={brandLogo}
            brandName={brandName}
            loginMethods={[
              LOGIN_METHOD.NOSTR,
              LOGIN_METHOD.GITHUB,
              LOGIN_METHOD.EMAIL_MAGIC_LINK,
              LOGIN_METHOD.GOOGLE,
              LOGIN_METHOD.OIDC,
            ]}
          />
        }
      />
      <Route
        path="/login/callback"
        element={
          <LoginCallbackPage
            setSession={setSession}
            titleClassName={classes.title}
            apiBaseUrl={API_BASE_URL}
          />
        }
      />
      <Route
        path="/login/2fa"
        element={
          <Login2FAPage
            setSession={setSession}
            verify2FA={useRustyState.getState().verify2FA}
            titleClassName={classes.title}
          />
        }
      />
      <Route
        path="/login/magic-link"
        element={
          <MagicLinkLoginPage
            loginChallenge={useRustyState.getState().loginChallenge}
            titleGradient={{ from: 'blue', to: 'teal' }}
            titleClassName={classes.title}
          />
        }
      />
      <Route
        path="/privacy"
        element={<PrivacyPage supportEmail="support@formshive.com" footer={<Footer />} />}
      />
      <Route
        path="/terms"
        element={<TermsPage supportEmail="support@formshive.com" footer={<Footer />} />}
      />
      <Route
        path="/signup"
        element={
          <SignupPage
            backgroundImage={isDark ? '/login-background_dark.jpg' : '/login-background.jpg'}
            loginMethods={[LOGIN_METHOD.NOSTR, LOGIN_METHOD.GITHUB, LOGIN_METHOD.EMAIL_MAGIC_LINK]}
          />
        }
      />
      <Route path="/account" element={<AccountHomePage />} />
      <Route path="/account/forms" element={<AccountFormsStartPage />} />
      <Route path="/account/forms/create" element={<AccountFormCreatePage />} />
      <Route path="/account/forms/:uuid" element={<AccountFormViewPage />} />
      <Route path="/account/forms/:uuid/fields" element={<AccountFormFieldsPage />} />
      <Route path="/account/integrations" element={<AccountIntegrationsStartPage />} />
      <Route path="/account/integrations/create" element={<AccountIntegrationCreatePage />} />
      <Route path="/account/integrations/:uuid" element={<AccountIntegrationViewPage />} />
      <Route
        path="/account/integrations/:uuid/deliveries"
        element={<AccountIntegrationDeliveriesPage />}
      />
      <Route path="/account/messages" element={<AccountMessagesStartPage />} />
      <Route path="/account/message/:messageId" element={<AccountMessageDetailPage />} />
      <Route path="/account/issues" element={<AccountIssuesStartPage />} />
      <Route path="/account/billing/subscriptions" element={<AccountBillingSubscriptionsPage />} />
      <Route path="/account/billing/usage" element={<AccountBillingUsagePage />} />
      <Route path="/account/referrals" element={<AccountReferralsPage />} />
      <Route path="/account/api-keys" element={<AccountApiKeysPage />} />
      <Route path="/account/mcp" element={<AccountMcpPage />} />
      <Route path="/account/security-log" element={<AccountSecurityLogPage />} />
      <Route path="/account/connected" element={<Navigate to="/account/profile" replace />} />
      <Route path="/account/linked" element={<AccountConnectedAccountsLandingPage />} />
      <Route path="/account/docs" element={<AccountDocsPage />} />
      <Route path="/account/data-privacy" element={<DataPrivacyPage />} />
      <Route
        path="/account/profile"
        element={
          <AccountProfilePage serviceDomain="formshive.com" serviceEmail="hello@formshive.com" />
        }
      />
      <Route
        path="/account/support"
        element={
          <SupportPage
            session={useRustyState.getState().api?.auth?.getSession()}
            serviceName="Formshive"
          />
        }
      />
    </Routes>
  );
}
